<template>
  <v-container fluid class="invoice-create-page pa-0">
    <div v-if="order" class="order-banner" :class="orderMatchClass">
      <div>
        <div class="banner-title">Order #{{ order.localId }}</div>
        <div class="banner-subtitle">
          Order Total: {{ order.total | currency }} · Invoice Total:
          {{ invoiceTotal | currency }}
        </div>
      </div>

      <v-btn small rounded outlined color="info" @click="viewOrder">
        <v-icon left small>mdi-eye-outline</v-icon>
        View Order
      </v-btn>
    </div>

    <v-alert
      v-if="selectedCustomer && selectedCustomer.alert"
      border="left"
      type="error"
      transition="slide-y-transition"
      dismissible
      class="mb-3"
      v-model="selectedCustomer.alert"
      color="red"
    >
      <strong>{{ selectedCustomer.note }}</strong>
    </v-alert>

    <v-alert
      v-else-if="selectedCustomer && selectedCustomer.note"
      border="left"
      transition="slide-y-transition"
      class="mb-3"
      color="blue"
      text
    >
      <strong>Note:</strong>
      <span class="ml-1">{{ selectedCustomer.note }}</span>
    </v-alert>

    <v-card outlined class="invoice-summary-card">
      <div class="invoice-summary">
        <div>
          <div class="invoice-create-title">
            Create Invoice
            <v-chip
              v-if="
                currentBusiness &&
                currentBusiness.metadata &&
                !currentBusiness.metadata.isSingleCustomerPricing &&
                selectedCustomer &&
                selectedCustomer.PriceTier
              "
              x-small
              class="ml-2 font-weight-bold"
              color="red"
              text-color="white"
            >
              {{ selectedCustomer.PriceTier.name }}
            </v-chip>
          </div>

          <div class="invoice-create-subtitle">
            {{
              selectedCustomer
                ? selectedCustomer.name
                : "Select customer to start"
            }}
          </div>
        </div>

        <div class="invoice-summary-actions">
          <div class="invoice-total">
            {{ invoiceTotal | currency }}
          </div>

          <v-btn
            rounded
            outlined
            :small="$vuetify.breakpoint.smAndDown"
            @click.stop.prevent="cancel"
          >
            Cancel
          </v-btn>

          <v-btn
            color="primary"
            rounded
            :small="$vuetify.breakpoint.smAndDown"
            :loading="loadingSaveBtn"
            :disabled="disabled"
            @click="saveInvoice"
          >
            <v-icon left small>mdi-content-save-outline</v-icon>
            Save
          </v-btn>

          <InvoiceJsonImporter @apply="applyJsonPayload" />
        </div>
      </div>
    </v-card>

    <v-card outlined class="customer-toggle-card mt-3">
      <div
        class="customer-toggle-header"
        @click="customerPanel = !customerPanel"
      >
        <div>
          <div class="customer-toggle-title">
            {{ selectedCustomer ? selectedCustomer.name : "Select Customer" }}
          </div>
          <div class="customer-toggle-subtitle">
            Tap to
            {{
              selectedCustomer
                ? "change customer and sales rep"
                : "choose customer and sales rep"
            }}
          </div>
        </div>

        <v-icon>{{
          customerPanel ? "mdi-chevron-up" : "mdi-chevron-down"
        }}</v-icon>
      </div>

      <v-expand-transition>
        <div v-show="customerPanel" class="customer-panel">
          <SelectCustomer
            :initCustomer="initCustomer"
            :selectCustomerError="selectCustomerError"
            @reset="selectCustomerError = []"
          />
        </div>
      </v-expand-transition>
    </v-card>

    <div v-if="selectedCustomer" class="mt-3">
      <ProductSearchBar @submit="addRow" @submitMultiple="addMultipleRows" />
    </div>

    <v-card v-else outlined class="start-card mt-3">
      <v-card-text class="text-center pa-6">
        <v-icon size="44" color="primary">mdi-account-search-outline</v-icon>
        <div class="start-title">Select customer to start invoice</div>
        <div class="start-subtitle">
          Product search and cart will appear after customer selection.
        </div>
      </v-card-text>
    </v-card>

    <div v-if="selectedCustomer" class="mt-3">
      <Cart />
    </div>

    <div class="mobile-save-bar d-sm-none">
      <div>
        <div class="mobile-total-label">Invoice Total</div>
        <div class="mobile-total-value">{{ invoiceTotal | currency }}</div>
      </div>

      <InvoiceJsonImporter @apply="applyJsonPayload" />

      <v-btn
        color="primary"
        rounded
        :loading="loadingSaveBtn"
        :disabled="disabled"
        @click="saveInvoice"
      >
        Save
      </v-btn>
    </div>
  </v-container>
</template>
<script>
import invoiceService from "../service";
import customerService from "@/modules/Customer/service";
import ProductSearchBar from "@/modules/Invoice/components/ProductSearchBar";
import Cart from "@/modules/Invoice/components/Cart";
import PriceTierService from "@/modules/Product/PriceTier/service.js";
import SelectCustomer from "@/modules/Invoice/components/SelectCustomer";
import InvoiceJsonImporter from "@/modules/Invoice/components/InvoiceJsonImporter";
import { mapActions, mapGetters } from "vuex";
import Hashids from "hashids";
const hashids = new Hashids();

export default {
  name: "create-invoice",

  data() {
    return {
      order: null,
      loadingSaveBtn: false,
      loading: true,
      customerPanel: true,
      selectCustomerError: [],
      initCustomer: null,
    };
  },
  watch: {
    selectedCustomer(val) {
      if (val) {
        this.customerPanel = false;
      }
    },
  },
  components: {
    Cart,
    SelectCustomer,
    ProductSearchBar,
    InvoiceJsonImporter,
  },

  async created() {
    this.ResetInvoice();
    this.SetEditInvoiceId(null);

    let CustomerId = null;

    if (this.$route.query && this.$route.query.CustomerId) {
      CustomerId = this.$route.query.CustomerId;
    }

    if (
      this.$route.params &&
      this.$route.params.passData &&
      this.$route.params.passData.CustomerId
    ) {
      CustomerId = this.$route.params.passData.CustomerId;
    }

    if (CustomerId) {
      const customer = await customerService.getById(CustomerId);
      if (customer) {
        this.initCustomer = customer.data;
      }
    }

    const obj = this.$route.params;

    if (obj && Object.keys(obj).length !== 0) {
      if (obj.type === "purchase") {
        obj.passData.map((val) => {
          this.AddInvoiceDetail({
            ProductId: val.Product.id,
            name: val.Product.name,
            price: val.Product.price,
            total: val.Product.price * val.qty,
            qty: val.qty,
            cost: val.cost | 0,
            imeiNumber: val.imeiNumber,
            previousPrice: val.Product.price,
          });
        });
      }

      if (obj.type === "order") {
        this.order = obj.passData;

        if (obj.passData.Customer && obj.passData.Customer.PriceTierId) {
          this.getPriceTier(obj.passData.Customer.PriceTierId).then(
            (priceTier) => {
              const isFixedDiscount =
                priceTier && priceTier.discountType === "fixed";
              const isPercentageDiscount =
                priceTier && priceTier.discountType === "percentage";

              if (obj.passData.OrderDetails.length > 0) {
                [...obj.passData.OrderDetails].reverse().map((val) => {
                  let usePrice = val.price;

                  if (isFixedDiscount) {
                    const matchingPrice = priceTier.ProductPrices.find(
                      (pp) => pp.ProductId === val.ProductId
                    );
                    if (matchingPrice) {
                      usePrice = matchingPrice.price;
                    }
                  }

                  if (isPercentageDiscount && priceTier.rate > 0) {
                    const discountAmount = (val.price * priceTier.rate) / 100;
                    usePrice = val.price - discountAmount;
                  }

                  this.AddInvoiceDetail({
                    ProductId: val.Product.id,
                    name: val.name,
                    price: usePrice,
                    total: usePrice * val.qty,
                    qty: val.qty,
                    cost: val.Product.avgCost || val.Product.cost || val.cost,
                    imeiNumber: val.imeiNumber,
                    previousPrice: val.Product.price,
                    Product: val.Product,
                    needIMEINumber: val.Product.trackSerialNumber
                      ? true
                      : false,
                    type: "order",
                  });
                });
              }
            }
          );
        } else if (obj.passData.OrderDetails.length > 0) {
          [...obj.passData.OrderDetails].reverse().map((val) => {
            this.AddInvoiceDetail({
              ProductId: val.Product.id,
              name: val.name,
              price: val.price,
              total: val.price * val.qty,
              qty: val.qty,
              cost: val.Product.avgCost || val.Product.cost || val.cost,
              imeiNumber: val.imeiNumber,
              previousPrice: val.Product.price,
              Product: val.Product,
              needIMEINumber: val.Product.trackSerialNumber ? true : false,
              type: "order",
            });
          });
        }
      }

      if (obj.type === "invoice") {
        if (obj.passData.InvoiceDetails.length) {
          obj.passData.InvoiceDetails.map((val) => {
            this.AddInvoiceDetail({
              ProductId: val.Product.id,
              name: val.name,
              price: val.price,
              total: val.price * val.qty,
              qty: val.qty,
              cost: val.cost | 0,
              previousPrice: val.Product.price,
              needIMEINumber: val.Product.trackSerialNumber ? true : false,
              type: "copyInvoice",
            });
          });
        }
      }
    }
  },

  computed: {
    ...mapGetters("invoice", [
      "Invoice",
      "InvoiceDetails",
      "invoiceTotal",
      "getSelectedCustomer",
      "getSelectedUser",
    ]),
    ...mapGetters("global", ["currentBusiness"]),

    orderMatchClass() {
      if (!this.order) return "";
      return parseFloat(this.order.total) === this.invoiceTotal
        ? "order-match"
        : "order-mismatch";
    },

    color() {
      let value = "yellow lighten-4";
      if (parseFloat(this.order.total) !== this.invoiceTotal)
        value = "red lighten-4";
      if (parseFloat(this.order.total) === this.invoiceTotal)
        value = "green lighten-4";
      return value;
    },

    disabled() {
      let value = this.InvoiceDetails.length === 0;

      this.InvoiceDetails.map((row) => {
        if (row.needIMEINumber && !row.delete) value = true;
      });

      if (!value) {
        if (
          this.currentBusiness.metadata &&
          this.currentBusiness.metadata.lockInvoiceSalesRep &&
          !this.getSelectedUser
        ) {
          value = true;
        }
      }

      return value;
    },

    selectedCustomer: {
      get() {
        return this.getSelectedCustomer;
      },
      set(val) {
        this.SetSelectedCustomer(val);
      },
    },
  },

  methods: {
    ...mapActions("invoice", [
      "AddInvoiceDetail",
      "ResetInvoice",
      "SetEditInvoiceId",
      "SetSelectedCustomer",
      "LoadInvoiceJson",
    ]),

    applyJsonPayload(payload) {
      this.LoadInvoiceJson(payload);
      this.initCustomer = payload.Customer || this.getSelectedCustomer;
      this.customerPanel = false;
    },

    getPriceTier(PriceTierId) {
      return PriceTierService.getById(PriceTierId).then((response) => {
        this.loading = false;
        return response.data;
      });
    },

    viewOrder() {
      const hashId = hashids.encode(this.order.id);
      const routeData = this.$router.resolve({
        path: `/global/order/${hashId}/view`,
      });
      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },

    cancel() {
      if (this.InvoiceDetails.length > 0) {
        this.$swal({
          title: "Are you sure?",
          text: "You want to cancel this invoice",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes",
          cancelButtonText: "No",
        }).then((result) => {
          if (result.value) {
            this.ResetInvoice();
            this.$router.go(-1);
          }
        });
      } else {
        this.ResetInvoice();
        this.$router.go(-1);
      }
    },

    saveInvoice() {
      if (this.order && parseFloat(this.order.total) !== this.invoiceTotal) {
        this.$swal({
          title: "Are you sure?",
          text: "Order total and invoice total is not matching",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes",
          cancelButtonText: "No",
        }).then((result) => {
          if (result.value) this.continueSaving();
        });
        return;
      }

      this.continueSaving();
    },

    continueSaving() {
      if (!this.Invoice.CustomerId) {
        this.selectCustomerError = ["Please Select Customer"];
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "Customer not selected!",
          });
        return false;
      }

      if (this.InvoiceDetails) {
        const filterInvoiceDetail = this.InvoiceDetails.filter(
          (item) => !item.delete
        );

        this.loadingSaveBtn = true;

        if (this.selectedCustomer && this.selectedCustomer.PriceTierId) {
          this.Invoice.PriceTierId = this.selectedCustomer.PriceTierId;
        }

        const saveInvoice = this.Invoice;
        saveInvoice.InvoiceDetails = filterInvoiceDetail;

        if (this.order) {
          saveInvoice.OrderId = this.order.id;
        }

        return invoiceService
          .create(saveInvoice)
          .then(() => {
            this.order = null;
            this.ResetInvoice();
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "success",
                title: "Invoice was created!",
              });
            this.loadingSaveBtn = false;
            this.$router.go(-1);
          })
          .catch((error) => {
            this.loadingSaveBtn = false;
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 6000,
              })
              .fire({
                icon: "error",
                title: error.data.message,
              });
          });
      }
    },

    addMultipleRows(products) {
      if (!products.length) return true;
      products.map((row) => {
        const total = row.Bar.price * row.Bar.qty;
        this.AddInvoiceDetail({
          ProductId: row.id,
          name: row.Bar.name,
          price: row.Bar.price,
          total,
          qty: row.Bar.qty,
          boxQty: row.Bar.boxQty,
          cost: row.cost | 0,
          unitCountInBox: row.unitCountInBox,
          unitWeight: parseFloat(row.weight),
          totalWeight: parseFloat(row.weight) * parseFloat(row.Bar.qty),
          weightPrice: parseFloat(row.weightboxPrice),
          imeiNumber: row.imeiNumber,
          previousPrice: row.Bar.price,
        });
      });
    },

    addRow(val) {
      if (!val) return;

      const total = val.Bar.price * val.Bar.qty;
      let costAmount = 0;

      if (
        this.currentBusiness &&
        this.currentBusiness.metadata &&
        this.currentBusiness.metadata.showAvgCostOnProduct
      ) {
        costAmount = val.avgCost || val.cost;
      } else {
        costAmount = val.cost;
      }

      this.AddInvoiceDetail({
        ProductId: val.id,
        name: val.Bar.name,
        price: val.Bar.price,
        total,
        qty: val.Bar.qty,
        boxQty: val.Bar.boxQty,
        cost: costAmount,
        unitCountInBox: val.unitCountInBox,
        imeiNumber: val.imeiNumber,
        unitWeight: val.weight,
        totalWeight: parseFloat(val.weight) * parseFloat(val.Bar.qty),
        weightPrice: val.weightboxPrice,
        previousPrice: val.Bar.price,
      });
    },
  },
};
</script>
<style scoped>
.invoice-create-page {
  background: #f8fafc;
  min-height: 100%;
  padding-bottom: 88px !important;
}

.invoice-summary-card,
.customer-toggle-card,
.start-card {
  border-radius: 20px;
  background: #ffffff;
  overflow: hidden;
}

.invoice-summary {
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.invoice-create-title {
  font-size: 22px;
  font-weight: 900;
  color: #0f172a;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.invoice-create-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.invoice-summary-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.invoice-total {
  font-size: 22px;
  font-weight: 900;
  color: #0f172a;
  margin-right: 6px;
}

.customer-toggle-header {
  padding: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.customer-toggle-title {
  font-size: 16px;
  font-weight: 900;
  color: #0f172a;
}

.customer-toggle-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.customer-panel {
  padding: 14px 16px;
  border-top: 1px solid #e2e8f0;
}

.order-banner {
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 14px 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #ffffff;
}

.order-match {
  background: #ecfdf5;
  border-color: #bbf7d0;
}

.order-mismatch {
  background: #fef2f2;
  border-color: #fecaca;
}

.banner-title {
  font-size: 15px;
  font-weight: 900;
  color: #0f172a;
}

.banner-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.start-title {
  font-size: 18px;
  font-weight: 900;
  color: #0f172a;
  margin-top: 10px;
}

.start-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
}

.mobile-save-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 -8px 24px rgba(15, 23, 42, 0.08);
}

.mobile-total-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
}

.mobile-total-value {
  font-size: 18px;
  font-weight: 900;
  color: #0f172a;
}

@media (max-width: 600px) {
  .invoice-create-page {
    padding: 10px !important;
    padding-bottom: 84px !important;
  }

  .invoice-summary-actions {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .invoice-total {
    grid-column: 1 / -1;
  }

  .invoice-summary-actions .v-btn {
    width: 100%;
  }

  .order-banner {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
